import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import {
  captureAttributionFromUrl,
  removeAttributionFromLocalStorage,
} from '@utils/captureAttribution';

/**
 * Guarda a origem de aquisição da entrada — mas só de quem ainda não tem conta aberta.
 *
 * ## Por que a sessão participa
 *
 * A origem existe para explicar um CADASTRO. Quem já está logado e abre uma URL de campanha não vai
 * se cadastrar: aquele clique rendeu uma visita, não uma conta. Guardar a campanha ali a deixaria no
 * navegador até vencer o prazo, pronta para ser carimbada num cadastro futuro — que pode nem ser
 * desta pessoa, num aparelho compartilhado — e, pela regra de primeiro toque, para bloquear a
 * campanha que de fato trouxer esse cadastro.
 *
 * Chegando autenticado, a origem guardada também é APAGADA: se existe sessão neste navegador, o
 * clique anterior já não pode ser o que explica um cadastro novo.
 *
 * ## Por que adiar é seguro
 *
 * Porque a query da entrada é um instantâneo tirado na avaliação do módulo (`utils/entrySearch.ts`),
 * e não uma leitura de `window.location` na hora. Esperar a sessão resolver não perde o parâmetro,
 * mesmo que a pessoa já tenha navegado para outra tela nesse meio-tempo.
 *
 * `loading` não decide nada: é o estado em que ainda não se sabe, e agir ali seria escolher pelo
 * lado errado na metade das vezes.
 *
 * ## A trava é só da captura
 *
 * GUARDAR acontece no máximo uma vez por documento: o que interessa é como a pessoa CHEGOU, e isso
 * não muda enquanto o documento é o mesmo. A sessão, essa muda — vence e é detectada no foco, e um
 * logout em outra aba chega por transmissão. Sem a trava, uma sessão autenticada na chegada que cai
 * depois faria o efeito rodar pelo ramo anônimo, e como a query da entrada é um instantâneo
 * imutável, a campanha recém-apagada renasceria idêntica.
 *
 * APAGAR não tem trava, e a assimetria é deliberada. A leitura inicial da sessão pode falhar — o
 * next-auth converte a falha em sessão nula e expõe `unauthenticated`, para só recuperar a sessão
 * real na releitura por foco. Travar a limpeza junto deixaria a campanha guardada por conta de um
 * estado que era erro de rede, e não anonimato. Apagar duas vezes não custa nada; deixar de apagar,
 * sim.
 */
export const useCapturaDeOrigem = () => {
  const { status } = useSession();
  const capturaEncerrada = useRef(false);

  useEffect(() => {
    if (status === 'loading') return;

    if (status === 'authenticated') {
      // Sabendo que há sessão, nenhuma queda posterior pode reabrir a captura.
      capturaEncerrada.current = true;
      removeAttributionFromLocalStorage();
      return;
    }

    if (capturaEncerrada.current) return;

    capturaEncerrada.current = true;
    captureAttributionFromUrl();
  }, [status]);
};
